import { useState, useRef, useCallback } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentArrowDownIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { importAPI } from '../utils/api';

const STEPS = {
  UPLOAD: 'upload',
  VALIDATING: 'validating',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  RESULTS: 'results'
};

export default function CSVImportModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setValidationResult(null);
    setImportResult(null);
    setError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please upload a CSV file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    setStep(STEPS.VALIDATING);
    setError(null);

    try {
      const response = await importAPI.validate(file);
      setValidationResult(response.data);
      setStep(STEPS.PREVIEW);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Validation failed';
      setError(errorMessage);
      setStep(STEPS.UPLOAD);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setStep(STEPS.IMPORTING);
    setError(null);

    try {
      const response = await importAPI.import(file);
      setImportResult(response.data);
      setStep(STEPS.RESULTS);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Import failed';
      setError(errorMessage);
      setStep(STEPS.PREVIEW);
    }
  };

  const handleDownloadTemplate = () => {
    importAPI.downloadTemplate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === STEPS.UPLOAD && 'Import Submissions from CSV'}
            {step === STEPS.VALIDATING && 'Validating CSV...'}
            {step === STEPS.PREVIEW && 'Preview Import'}
            {step === STEPS.IMPORTING && 'Importing...'}
            {step === STEPS.RESULTS && 'Import Complete'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Upload Step */}
          {step === STEPS.UPLOAD && (
            <div className="space-y-6">
              {/* Download Template */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <DocumentArrowDownIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-800">
                      Download the CSV template to ensure your data is formatted correctly.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50'
                    : file
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {file ? (
                  <div className="space-y-2">
                    <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      onClick={() => setFile(null)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 mx-auto" />
                    <p className="text-gray-600">
                      Drag and drop your CSV file here, or{' '}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-primary-600 hover:text-primary-800 font-medium"
                      >
                        browse
                      </button>
                    </p>
                    <p className="text-xs text-gray-500">Maximum 500 rows per import</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Validating Step */}
          {step === STEPS.VALIDATING && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Validating your CSV file...</p>
            </div>
          )}

          {/* Preview Step */}
          {step === STEPS.PREVIEW && validationResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Total Rows</p>
                  <p className="text-2xl font-bold text-gray-900">{validationResult.summary.total_rows}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Will Import</p>
                  <p className="text-2xl font-bold text-green-700">{validationResult.summary.would_import}</p>
                </div>
                {validationResult.summary.would_skip_duplicates > 0 && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-yellow-600">Duplicates (will skip)</p>
                    <p className="text-2xl font-bold text-yellow-700">{validationResult.summary.would_skip_duplicates}</p>
                  </div>
                )}
                {validationResult.summary.invalid_rows > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-600">Invalid Rows</p>
                    <p className="text-2xl font-bold text-red-700">{validationResult.summary.invalid_rows}</p>
                  </div>
                )}
              </div>

              {/* Row limit warning */}
              {validationResult.validation.exceeds_row_limit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-800">
                      Your file exceeds the maximum limit of {validationResult.validation.max_rows} rows.
                      Please split your file into smaller batches.
                    </p>
                  </div>
                </div>
              )}

              {/* Preview of valid rows */}
              {validationResult.preview.first_5_valid.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Preview (first 5 rows)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Organization</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Phone</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {validationResult.preview.first_5_valid.map((row) => (
                          <tr key={row.row}>
                            <td className="px-3 py-2 text-gray-500">{row.row}</td>
                            <td className="px-3 py-2 text-gray-900">{row.organization_name}</td>
                            <td className="px-3 py-2 text-gray-900">{row.phone_number}</td>
                            <td className="px-3 py-2 text-gray-900">{row.contact_email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Duplicate warnings */}
              {validationResult.preview.duplicates.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 mb-2">
                    <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                    Duplicates (will be skipped)
                  </h3>
                  <div className="bg-yellow-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <ul className="text-sm text-yellow-800 space-y-1">
                      {validationResult.preview.duplicates.map((dup) => (
                        <li key={dup.row}>
                          Row {dup.row}: {dup.organization_name} - {dup.phone_number}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Validation errors */}
              {validationResult.preview.errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-800 mb-2">
                    <XCircleIcon className="h-4 w-4 inline mr-1" />
                    Validation Errors
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <ul className="text-sm text-red-800 space-y-1">
                      {validationResult.preview.errors.map((err, idx) => (
                        <li key={idx}>
                          Row {err.row}: {err.errors.map(e => e.message).join(', ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Importing Step */}
          {step === STEPS.IMPORTING && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Importing submissions...</p>
              <p className="text-sm text-gray-500">This may take a moment. Creating Zendesk tickets...</p>
            </div>
          )}

          {/* Results Step */}
          {step === STEPS.RESULTS && importResult && (
            <div className="space-y-6">
              {/* Success message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <p className="text-sm text-green-800">{importResult.message}</p>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Imported</p>
                  <p className="text-2xl font-bold text-green-700">{importResult.summary.imported}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Zendesk Tickets Created</p>
                  <p className="text-2xl font-bold text-blue-700">{importResult.summary.zendesk_tickets_created}</p>
                </div>
                {importResult.summary.skipped_duplicates > 0 && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-yellow-600">Skipped (Duplicates)</p>
                    <p className="text-2xl font-bold text-yellow-700">{importResult.summary.skipped_duplicates}</p>
                  </div>
                )}
                {importResult.summary.invalid_rows > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-600">Invalid Rows</p>
                    <p className="text-2xl font-bold text-red-700">{importResult.summary.invalid_rows}</p>
                  </div>
                )}
              </div>

              {/* Imported submissions */}
              {importResult.results.imported.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Imported Submissions</h3>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <ul className="text-sm text-gray-700 space-y-1">
                      {importResult.results.imported.slice(0, 20).map((item) => (
                        <li key={item.id}>
                          {item.organization_name} - {item.phone_number}
                        </li>
                      ))}
                      {importResult.results.imported.length > 20 && (
                        <li className="text-gray-500">
                          ...and {importResult.results.imported.length - 20} more
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Skipped rows */}
              {importResult.results.skipped.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 mb-2">Skipped (Duplicates)</h3>
                  <div className="bg-yellow-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <ul className="text-sm text-yellow-800 space-y-1">
                      {importResult.results.skipped.map((item, idx) => (
                        <li key={idx}>
                          Row {item.row}: {item.organization_name} - {item.phone_number}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Failed rows */}
              {importResult.results.failed.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-800 mb-2">Failed</h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <ul className="text-sm text-red-800 space-y-1">
                      {importResult.results.failed.map((item, idx) => (
                        <li key={idx}>
                          Row {item.row}: {item.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg border-t border-gray-200">
          {step === STEPS.UPLOAD && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleValidate}
                disabled={!file}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  file
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Validate & Preview
              </button>
            </>
          )}

          {step === STEPS.PREVIEW && (
            <>
              <button
                onClick={() => {
                  setStep(STEPS.UPLOAD);
                  setFile(null);
                  setValidationResult(null);
                  setError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={!validationResult?.validation?.is_valid || validationResult?.summary?.would_import === 0}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  validationResult?.validation?.is_valid && validationResult?.summary?.would_import > 0
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Import {validationResult?.summary?.would_import || 0} Submissions
              </button>
            </>
          )}

          {step === STEPS.RESULTS && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
